import React, { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Shared Modal component
 *
 * Props:
 *   open       boolean  — controls visibility
 *   onClose    fn       — called on backdrop click or X button
 *   title      string   — modal heading
 *   subtitle   string?  — optional sub-heading
 *   size       'sm'|'md'|'lg'|'xl'  — max-width (default 'md')
 *   align      'center'|'top'       — vertical alignment (default 'center')
 *   children   ReactNode
 *   footer     ReactNode?   — rendered inside .modal-footer
 *   noPad      boolean?     — skip .modal-body padding (for custom content)
 */
export default function Modal({
  open, onClose, title, subtitle, size = 'md',
  align = 'center', children, footer, noPad = false,
}) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const maxW = { sm: 400, md: 500, lg: 680, xl: 860 }[size] || 500;
  const overlayClass = align === 'top' ? 'modal-overlay-top' : 'modal-overlay';

  return (
    <div className={overlayClass} onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="modal-box" style={{ maxWidth: maxW }}>
        {/* Header */}
        {(title || subtitle) && (
          <div className="modal-header">
            <div className="min-w-0 pr-2">
              {title && <h2>{title}</h2>}
              {subtitle && <p>{subtitle}</p>}
            </div>
            <button className="modal-close" onClick={onClose} type="button">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Body */}
        {noPad ? children : <div className="modal-body">{children}</div>}

        {/* Footer */}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/** Confirm/Alert dialog — simpler destructive action pattern */
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false, loading = false }) {
  useEffect(() => {
    if (!open) return;
    const h = e => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="confirm-box">
        {/* Icon */}
        <div className="confirm-icon" style={{ background: danger ? '#FEF2F2' : '#FFFBEB' }}>
          {danger
            ? <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            : <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="#F59E0B" strokeWidth="2"/><path d="M12 8v4M12 16h.01" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round"/></svg>
          }
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>{title}</h3>
        {message && <p style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.6, marginBottom: 20 }}>{message}</p>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button
            className={danger ? 'btn-danger' : 'btn-primary'}
            onClick={onConfirm}
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** FormField — label + input wrapper */
export function Field({ label, required, error, children, hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <label className="field-label">
          {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
        </label>
      )}
      {children}
      {hint && !error && <p style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 4 }}>{hint}</p>}
      {error && <p style={{ fontSize: 11.5, color: '#EF4444', marginTop: 4 }}>{error}</p>}
    </div>
  );
}
